/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable @typescript-eslint/naming-convention */

import { Get } from 'config/apiServiceConfig'
import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { Product } from '../@types'
import {
  BomCategoryType,
  CategoryL1Type,
  GetActiveProductCategoriesType,
  StatusSourceType,
  InventoryStatusNotesParamsType,
  InventoryStatusNotesItemType,
} from '../@types/Product'

import { ProductAddEditPayloadType, PutUpdateProductStatusPayloadType } from '../@types/ProductAdd'

type GetProductsResponseType = {
  data: Product.ProductType[]
  pagination: Product.PaginationType
}

export type GetProductTypeResponseType = {
  data: {
    product_type_id: number
    product_type_name: string
  }[]
}

type GetProductInventoryStatusResponseType = {
  data: {
    status: Product.ProductInventoryStatusType[]
  }
}

export type GetProductInventoryStatusNotesResponseType = {
  data: {
    notes: Product.ProductInventoryStatusNotesType[]
  }
}

export type GetProductStockHistoryV2ResponseType = {
  data: Product.ProductStockHistoryV2Type[]
  pagination: {
    direction: string
    number_of_elements: number
    page_index: number
    page_size: number
    sort: string
    total_elements: number
    total_pages: number
  }
}

type GetProductStockHistoryV2ParamsType = {
  'pagination.page_size': number
  'pagination.page_index': number
  product_id: number
  location_id: number
  status_id?: number
  created_date_from?: number
  created_date_to?: number
}

export type GetProductListResponseType<ItemType = Product.ProductListType> = {
  data: ItemType[]
  pagination: Product.PaginationInKongType
}

export type GetProductListParamsType = {
  'pagination.sort'?: string
  'pagination.direction'?: string
  'pagination.page_size': number
  'pagination.page_index': number
  'filter.name'?: string
  'filter.category_ids'?: string
  'filter.is_active'?: boolean
  'filter.product_type'?: string
  'filter.bom_category'?: string
  'filter.brand_ids'?: string
  'filter.product_type_ids'?: string
}

type GetProductCategoryL1ResponseType = CategoryL1Type[]

export type GetUomListParamType = {
  groups: string
  active?: boolean
  pageSize: number
  key?: string
}

export type GetCategoriesChildResponseType = CategoryL1Type & {
  category_childs: CategoryL1Type[]
}

export type GetStatusSourceResponseType = {
  data: StatusSourceType[]
}

export type GetBomCategoryResponseType = {
  data: BomCategoryType[]
}

export type GetVendorCountResponseType = {
  data: {
    vendorCount: number
  }
}

export type GetWimsProductTypeResponseType = {
  id: number
  name: string
}

export const getProducts = (params: Partial<Product.QueryType>) =>
  Get<GetProductsResponseType>({
    url: '/api/ims/:version/products',
    version: 'v2',
    params,
  })

export const getSkuRegistrationList = () => Get
export const getProductType = () =>
  Get<GetProductTypeResponseType>({
    url: '/api/product/type',
  })

export const getWimsProductType = () =>
  axiosInstanceApiGateway.Get<GetWimsProductTypeResponseType[]>({
    url: '/wims/internal/v1/products/types',
  })

export const getProductById = (productId: number) =>
  Get<Product.ProductV1Type>({
    url: '/api/product/:productId',
    urlParams: { productId },
  })

export const getProductInventoryStatus = () =>
  Get<GetProductInventoryStatusResponseType>({
    url: '/api/inventory-status',
  })

export const getProductStockHistoryV2 = (params: GetProductStockHistoryV2ParamsType) =>
  axiosInstanceApiGateway.Get({
    url: '/ims/internal/v2/inventories/stock-history',
    params,
  })

export const getProductList = (params: GetProductListParamsType) =>
  axiosInstanceApiGateway.Get<Product.ProductListType[]>({
    url: '/wims/internal/v1/products',
    params,
  })

export const getProductListInQueryString = <ItemType = Product.ProductListType>(qs: string) =>
  axiosInstanceApiGateway.Get<GetProductListResponseType<ItemType>, true>({
    url: `/ims/internal/v2/products?${qs}`,
  })

export const getProductCategoryL1 = () =>
  Get<GetProductCategoryL1ResponseType>({
    url: '/api/categories',
  })

export const getStorageHandlingList = () =>
  Get<{ data: Product.StorageHandlingItemType[] }>({ url: `/api/product/storage-handling` })

export const getUomList = (params: GetUomListParamType) =>
  Get<{ data: Product.GetUomResponseType }>({ url: `/api/uoms`, params })

type GetWimsUomListParamsType = {
  'pagination.page_index': number
  'pagination.page_size': number
  'filter.active'?: boolean
  'filter.groups': string
  'filter.id'?: string
  'filter.name'?: string
}

export const getWimsUomList = (params: GetWimsUomListParamsType) =>
  axiosInstanceApiGateway.Get<{ data: Product.UomItemType[] }, true>({
    url: '/wims/internal/v1/products/uoms',
    params,
  })

export const getCategoriesChild = () =>
  Get<GetCategoriesChildResponseType[]>({ url: '/api/categories/child' })

export const getProductExchangeInfo = (id: number) =>
  axiosInstanceApiGateway.Get<Product.ProductExchangeInfoDetailType>({
    url: `/ims/internal/v1/products/${id}/exchange-info`,
  })

type GetProductCategoriesRequestType = {
  name?: string
  is_catalogue: boolean
}

export const getProductCategories = (params: GetProductCategoriesRequestType) =>
  axiosInstanceApiGateway.Get<GetActiveProductCategoriesType>({
    url: '/browse/internal/v1/active-categories',
    params,
  })

type WimsCategoryItemType = {
  id: number
  name: string
  msltc: number
  mslod: number
  mslor: number
  children: WimsCategoryItemType[]
}

type GetWimsCategoriesResponseType = {
  data: WimsCategoryItemType[]
}

export const getWimsCategories = (params: { name?: string }) =>
  axiosInstanceApiGateway.Get<GetWimsCategoriesResponseType, true>({
    url: '/wims/internal/v1/categories',
    params: {
      'pagination.page_index': 1,
      'pagination.page_size': 50,
      'pagination.direction': 'desc',
      ...(params.name && { 'filter.name': params.name }),
    },
  })

export const putUpdateProductStatusApi = (
  productId: number,
  payload: PutUpdateProductStatusPayloadType,
) =>
  axiosInstanceApiGateway.Put({
    url: '/wims/internal/:version/products/:productId/active',
    urlParams: { productId },
    version: 'v1',
    data: payload,
  })

export const getStatusSourceApi = () =>
  Get<GetStatusSourceResponseType>({
    url: '/api/product/status-source',
  })

export const getBomCategoryApi = () =>
  Get<GetBomCategoryResponseType>({
    url: '/api/product/bom-category',
  })

export const getVendorCountApi = (productId: number) =>
  Get<GetVendorCountResponseType>({
    url: '/api/ims/v2/product/:productId/vendor-count',
    urlParams: { productId },
  })

// To:do Remove product vendor feature flag after stable release
export const getProductVendorFeatureFlagApi = () =>
  axiosInstanceApiGateway.Get<
    { data: { FF_ENABLE_VENDOR_CATALOG_MIGRATION_ALLOYDB: string } },
    true
  >({
    url: '/commercial/internal/:version/feature-flags?flags=FF_ENABLE_VENDOR_CATALOG_MIGRATION_ALLOYDB',
    version: 'v1',
  })

export const getProductVendorCounts = (product_ids: string) =>
  axiosInstanceApiGateway.Get<{ data: { product_id: number; total_vendor: number }[] }, true>({
    url: '/commercial/internal/:version/product-vendors/vendor-counts',
    version: 'v1',
    params: {
      product_ids,
    },
  })

export const postCreateProductApi = (payload: ProductAddEditPayloadType) =>
  axiosInstanceApiGateway.Post({
    url: '/wims/internal/v1/products',
    data: payload,
  })

export const putUpdateProductApi = (id: number, payload: PutUpdateProductStatusPayloadType) =>
  axiosInstanceApiGateway.Put({
    url: '/wims/internal/v1/products/:id',
    data: payload,
    urlParams: { id },
  })

export const getInventoryStatusNotes = (params: InventoryStatusNotesParamsType) =>
  axiosInstanceApiGateway.Get<{ data: InventoryStatusNotesItemType[] }, true>({
    url: '/wims/internal/v1/inventory-status-notes',
    params,
  })
